<template>
  <div class="puzzle-wrapper">
    <div class="puzzle-controls">
      <button class="restart-btn" @click="restartGame">重新开始</button>
    </div>
    <div class="puzzle-container">
      <div
        v-for="(piece, index) in pieces"
        :key="piece.id"
        class="puzzle-piece"
        :class="{
          'active-piece': activeIndex === index,
          'empty-piece': piece.isEmpty,
        }"
        :style="piece.style"
        @click="handleMove(index)"
      >
        <img
          :src="piece.image"
          alt="puzzle piece"
          :class="{ 'empty-image': piece.isEmpty }"
        />
      </div>
    </div>
    <div v-if="showSuccess" class="success-overlay">
      <div class="success-message">
        <h2>拼图完成！🎉</h2>
        <button @click="restartGame">再玩一次</button>
      </div>
    </div>
  </div>
</template>

<script>
const PUZZLE_CONFIG = {
  ROWS: 3,
  COLS: 3,
};

export default {
  data() {
    return {
      originalPieces: this.generatePieces(),
      pieces: [],
      correctPositions: null,
      activeIndex: -1,
      showSuccess: false,
    };
  },

  mounted() {
    this.initializeGame();
  },

  methods: {
    generatePieces() {
      const pieces = [];
      let count = 0;

      for (let row = 0; row < PUZZLE_CONFIG.ROWS; row++) {
        for (let col = 0; col < PUZZLE_CONFIG.COLS; col++) {
          pieces.push({
            id: count,
            image: require(`@/assets/images/piece${count + 1}.png`),
            originalPosition: count,
            currentPosition: count,
            isEmpty: count === PUZZLE_CONFIG.ROWS * PUZZLE_CONFIG.COLS - 1,
            style: null,
          });
          count++;
        }
      }
      return pieces;
    },
    initializeGame() {
      this.correctPositions = this.originalPieces.map((p) => ({
        id: p.id,
        originalPosition: p.originalPosition,
      }));

      // 从正确位置开始初始化
      let shuffled = [...this.originalPieces];
      let emptyIndex = shuffled.length - 1; // 初始空白块位置（右下角）

      // 执行100次随机合法移动
      for (let i = 0; i < 100; i++) {
        const neighbors = [];
        const col = emptyIndex % PUZZLE_CONFIG.COLS;
        const row = Math.floor(emptyIndex / PUZZLE_CONFIG.COLS);

        // 寻找所有相邻可移动块
        if (col > 0) neighbors.push(emptyIndex - 1); // 左
        if (col < PUZZLE_CONFIG.COLS - 1) neighbors.push(emptyIndex + 1); // 右
        if (row > 0) neighbors.push(emptyIndex - PUZZLE_CONFIG.COLS); // 上
        if (row < PUZZLE_CONFIG.ROWS - 1)
          neighbors.push(emptyIndex + PUZZLE_CONFIG.COLS); // 下

        // 随机选择一个相邻块交换
        const randomNeighbor =
          neighbors[Math.floor(Math.random() * neighbors.length)];
        [shuffled[emptyIndex], shuffled[randomNeighbor]] = [
          shuffled[randomNeighbor],
          shuffled[emptyIndex],
        ];
        emptyIndex = randomNeighbor;
      }

      this.pieces = shuffled;

      this.$nextTick(() => {
        this.activeIndex = this.findEmptyPiece();
        setTimeout(() => (this.activeIndex = -1), 3000);
      });

      this.updatePositions();
    },

    updatePositions() {
      this.pieces.forEach((piece, index) => {
        const col = index % PUZZLE_CONFIG.COLS;
        const row = Math.floor(index / PUZZLE_CONFIG.COLS);

        piece.style = {
          transform: `translate(
            calc(${col} * 100% + ${col} * 1px),
            calc(${row} * 100% + ${row} * 1px)
          )`,
          width: "calc(100% / 3 - 1px)",
          height: "calc(100% / 3 - 1px)",
        };
      });
    },

    handleMove(clickedIndex) {
      const emptyIndex = this.findEmptyPiece();

      if (this.isMovable(clickedIndex, emptyIndex)) {
        this.activeIndex = clickedIndex;
        this.swapPieces(clickedIndex, emptyIndex);
        this.updatePositions();
        this.checkCompletion();

        this.$nextTick(() => {
          this.activeIndex = this.findEmptyPiece();
        });
      }

      setTimeout(() => (this.activeIndex = -1), 300);
    },

    findEmptyPiece() {
      return this.pieces.findIndex((p) => p.isEmpty);
    },

    isMovable(clickedIndex, emptyIndex) {
      const clickedRow = Math.floor(clickedIndex / PUZZLE_CONFIG.COLS);
      const clickedCol = clickedIndex % PUZZLE_CONFIG.COLS;
      const emptyRow = Math.floor(emptyIndex / PUZZLE_CONFIG.COLS);
      const emptyCol = emptyIndex % PUZZLE_CONFIG.COLS;

      return (
        (Math.abs(clickedRow - emptyRow) === 1 && clickedCol === emptyCol) ||
        (Math.abs(clickedCol - emptyCol) === 1 && clickedRow === emptyRow)
      );
    },

    swapPieces(indexA, indexB) {
      [this.pieces[indexA], this.pieces[indexB]] = [
        this.pieces[indexB],
        this.pieces[indexA],
      ];
    },

    checkCompletion() {
      const isSolved = this.pieces.every(
        (piece, index) =>
          piece.originalPosition ===
          this.correctPositions[index].originalPosition
      );

      if (isSolved) {
        this.showSuccess = true;
        this.$emit("puzzle-completed");
      }
    },

    restartGame() {
      this.showSuccess = false;
      this.initializeGame();
    },
  },
};
</script>

<style scoped>
.puzzle-wrapper {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%; /* 改为100%宽度 */
  max-width: 560px; /* 保持最大宽度 */
  margin: 0 auto;
  padding: 10px;
  box-sizing: border-box; /* 新增 */
}

.puzzle-controls {
  text-align: center;
  margin-bottom: 15px;
}

.restart-btn {
  padding: 12px 24px;
  font-size: 16px;
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.restart-btn:hover {
  background: #40a9ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}

.puzzle-container {
  position: relative;
  width: 100%; /* 改为基于父容器宽度 */
  height: 0; /* 使用padding-bottom实现正方形 */
  padding-bottom: 100%;
  max-width: 560px;
  margin: 10px auto;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.puzzle-piece {
  position: absolute;
  box-sizing: border-box;
  background: #fff;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.puzzle-piece img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 1px;
}

.active-piece {
  border: 3px solid #1890ff !important;
  box-shadow: 0 0 8px rgba(24, 144, 255, 0.3);
  z-index: 2;
}

.empty-piece {
  background: rgba(240, 242, 245, 0.4) !important;
  border: 3px solid rgba(255, 214, 102, 0.8) !important;
  backdrop-filter: blur(1px);
}
.empty-image {
  opacity: 0.4 !important;
  filter: grayscale(80%) brightness(1.1);
  mix-blend-mode: multiply;
}

@keyframes glowing {
  0% {
    box-shadow: 0 0 5px rgba(255, 214, 102, 0.4);
  }
  50% {
    box-shadow: 0 0 15px rgba(255, 214, 102, 0.6);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 214, 102, 0.4);
  }
}

.success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.success-message {
  background: white;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.success-message h2 {
  color: #1890ff;
  margin-bottom: 20px;
  font-size: 28px;
}

.success-message button {
  padding: 12px 30px;
  font-size: 18px;
  background: linear-gradient(135deg, #1890ff, #40a9ff);
  color: white;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: transform 0.3s;
}

.success-message button:hover {
  transform: scale(1.05);
}

@media (max-width: 600px) {
  .puzzle-wrapper {
    padding: 10px 5px; /* 减少左右padding */
  }

  .puzzle-container {
    margin: 5px auto;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1); /* 减少阴影偏移 */
  }
}
</style>
